import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiErr, PDF_BASE } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { fmtMoney, fmtCur, fmtTotals, sumByCurrency, fmtDateTime, ORDER_STATUSES, statusMeta } from "@/lib/format";
import { ArrowLeft, FilePdf, Receipt, PencilSimple } from "@phosphor-icons/react";
import { useAuth, can } from "@/context/AuthContext";
import { PhotoLightbox } from "@/components/PhotoLightbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (p, w) => `${BACKEND_URL}/api/files/${p.path}${w ? `?w=${w}` : ""}`;
const PAY_STATUS = { paid: { label: "Оплачено", color: "#34C759" }, partial: { label: "Частково оплачено", color: "#FFCC00" }, unpaid: { label: "Не оплачено", color: "#FF3B30" } };

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const [lb, setLb] = useState(null);

  const load = () => api.get(`/orders/${id}`).then((r) => setO(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!o) return <div className="text-muted-foreground">Завантаження...</div>;

  const changeStatus = async (status) => {
    try { await api.patch(`/orders/${id}/status`, { status }); toast.success("Статус змінено"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const makeInvoice = async () => {
    try { const r = await api.post(`/orders/${id}/invoice`); toast.success(`Рахунок ${r.data.number} створено`); navigate("/finance"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const st = statusMeta(o.status);

  return (
    <div className="w-full">
      <button onClick={() => navigate("/orders")} className="flex items-center gap-1 text-sm text-muted-foreground mb-3 hover:text-foreground" data-testid="back-btn">
        <ArrowLeft size={16} /> До нарядів
      </button>
      <PageHeader
        title={`Наряд ${o.number}`}
        subtitle={`Створено ${fmtDateTime(o.created_at)}`}
        action={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => window.open(`${PDF_BASE}/orders/${id}/pdf`, "_blank")} data-testid="order-pdf-btn">
              <FilePdf size={18} className="mr-1" /> PDF наряду
            </Button>
            <Button variant="outline" onClick={() => navigate(`/orders/${id}/edit`)} data-testid="edit-order-btn">
              <PencilSimple size={18} className="mr-1" /> Редагувати
            </Button>
            {can(user, "finance") && (
              <Button onClick={makeInvoice} data-testid="create-invoice-btn">
                <Receipt size={18} className="mr-1" /> Виставити рахунок
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
        {o.vehicle_photo && (
          <Card className="p-0 overflow-hidden md:col-span-1" data-testid="order-vehicle-photo-card">
            <button type="button" onClick={() => setLb({ photos: [o.vehicle_photo], index: 0 })} className="w-full h-full min-h-[160px] bg-secondary/40 cursor-pointer" data-testid="order-vehicle-photo">
              <img src={photoUrl(o.vehicle_photo, 500)} alt={o.vehicle_label} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </button>
          </Card>
        )}
        <Card className={`p-5 ${o.vehicle_photo ? "md:col-span-2" : "md:col-span-3"}`}>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="text-muted-foreground">Клієнт</div><div className="font-semibold">{o.client_name}</div></div>
            <div><div className="text-muted-foreground">Авто</div><div className="font-semibold">{o.vehicle_label}</div></div>
            <div><div className="text-muted-foreground">Майстер</div><div className="font-semibold">{o.master_name || "—"}</div></div>
            <div><div className="text-muted-foreground">Пробіг</div><div className="font-semibold">{o.mileage || "—"} км</div></div>
          </div>
          {o.complaint && <div className="mt-4 text-sm"><div className="text-muted-foreground">Скарги</div><div>{o.complaint}</div></div>}
        </Card>
        <Card className="p-5">
          <div className="text-sm text-muted-foreground mb-2">Статус</div>
          <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold mb-3" style={{ background: st.color + "22", color: st.color }}>
            {st.label}
          </span>
          <Select value={o.status} onValueChange={changeStatus}>
            <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{ORDER_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>

          <div className="mt-4 pt-4 border-t border-border" data-testid="payment-status">
            <div className="text-sm text-muted-foreground mb-2">Оплата</div>
            {o.payment?.invoiced ? (
              <>
                <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold"
                  style={{ background: (PAY_STATUS[o.payment.status]?.color || "#8E8E93") + "22", color: PAY_STATUS[o.payment.status]?.color || "#8E8E93" }}>
                  {PAY_STATUS[o.payment.status]?.label || o.payment.status}
                </span>
                <div className="text-xs text-muted-foreground mt-2">Рахунок {o.payment.invoice_number}</div>
                {o.payment.status !== "paid" && (
                  <div className="text-xs mt-1">До сплати: <span className="font-semibold text-foreground">{fmtTotals(o.payment.balance_by_currency)}</span></div>
                )}
              </>
            ) : (
              <span className="text-sm text-muted-foreground">Рахунок не виставлено</span>
            )}
          </div>
        </Card>
      </div>

      {(o.diagnostics_text || o.battery_segments > 0 || (o.diagnostics_photos && o.diagnostics_photos.length > 0)) && (
        <Card className="p-6 mb-4" data-testid="diagnostics-view">
          <h3 className="font-head font-bold mb-3">Вхідна діагностика</h3>
          {o.diagnostics_text && <p className="text-sm mb-3 whitespace-pre-wrap">{o.diagnostics_text}</p>}
          {o.battery_segments > 0 && (
            <div className="text-sm mb-3">
              <span className="text-muted-foreground">Батарея: {o.battery_segments} S. </span>
              {o.battery_bad_segments && o.battery_bad_segments.length > 0 ? (
                <span>Несправні сегменти ({o.battery_bad_segments.length}): <span className="font-semibold text-destructive">{o.battery_bad_segments.join(", ")}</span></span>
              ) : <span className="text-green-600">несправних сегментів не виявлено</span>}
            </div>
          )}
          {o.diagnostics_photos && o.diagnostics_photos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {o.diagnostics_photos.map((p, i) => (
                <button key={p.id || i} type="button" onClick={() => setLb({ photos: o.diagnostics_photos, index: i })}
                  className="aspect-square rounded-md overflow-hidden border border-border bg-secondary cursor-pointer" data-testid={`diag-view-photo-${i}`}>
                  <img src={photoUrl(p, 600)} alt={p.filename || "фото"} loading="lazy" decoding="async" className="w-full h-full object-cover hover:opacity-90 transition-opacity" />
                </button>
              ))}
            </div>
          )}
        </Card>
      )}

      {(o.repair_text || (o.repair_photos && o.repair_photos.length > 0)) && (
        <Card className="p-6 mb-4" data-testid="repair-view">
          <h3 className="font-head font-bold mb-3">Ремонтні роботи</h3>
          {o.repair_text && <p className="text-sm mb-3 whitespace-pre-wrap" data-testid="repair-text-view">{o.repair_text}</p>}
          {o.repair_photos && o.repair_photos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {o.repair_photos.map((p, i) => (
                <button key={p.id || i} type="button" onClick={() => setLb({ photos: o.repair_photos, index: i })}
                  className="aspect-square rounded-md overflow-hidden border border-border bg-secondary cursor-pointer" data-testid={`repair-view-photo-${i}`}>
                  <img src={photoUrl(p, 600)} alt={p.filename || "фото"} loading="lazy" decoding="async" className="w-full h-full object-cover hover:opacity-90 transition-opacity" />
                </button>
              ))}
            </div>
          )}
        </Card>
      )}

      {(o.exit_diagnostics_text || (o.exit_diagnostics_photos && o.exit_diagnostics_photos.length > 0)) && (
        <Card className="p-6 mb-4" data-testid="exit-diagnostics">
          <h3 className="font-head font-bold mb-3">Вихідна діагностика</h3>
          {o.exit_diagnostics_text && <p className="text-sm mb-3 whitespace-pre-wrap" data-testid="exit-text-view">{o.exit_diagnostics_text}</p>}
          {o.exit_diagnostics_photos && o.exit_diagnostics_photos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {o.exit_diagnostics_photos.map((p, i) => (
                <button key={p.id || i} type="button" onClick={() => setLb({ photos: o.exit_diagnostics_photos, index: i })}
                  className="aspect-square rounded-md overflow-hidden border border-border bg-secondary cursor-pointer" data-testid={`exit-view-photo-${i}`}>
                  <img src={photoUrl(p, 600)} alt={p.filename || "фото"} loading="lazy" decoding="async" className="w-full h-full object-cover hover:opacity-90 transition-opacity" />
                </button>
              ))}
            </div>
          )}
        </Card>
      )}

      <Card className="p-6 mb-4">
        <h3 className="font-head font-bold mb-3">Роботи</h3>
        {o.services.map((s, i) => (
          <div key={i} className="flex justify-between text-sm py-2 border-b border-border last:border-0">
            <span>{s.name} × {s.qty}</span><span className="font-semibold">{fmtCur(s.price * s.qty, s.currency)}</span>
          </div>
        ))}
        {o.services.length === 0 && <div className="text-sm text-muted-foreground">Немає</div>}
      </Card>

      <Card className="p-6 mb-4">
        <h3 className="font-head font-bold mb-3">Запчастини</h3>
        {o.parts.map((p, i) => (
          <div key={i} className="flex justify-between text-sm py-2 border-b border-border last:border-0">
            <span>{p.name} × {p.qty}</span><span className="font-semibold">{fmtCur(p.price * p.qty, p.currency)}</span>
          </div>
        ))}
        {o.parts.length === 0 && <div className="text-sm text-muted-foreground">Немає</div>}
      </Card>

      <Card className="p-6 flex justify-between items-center">
        <span className="text-muted-foreground">Роботи {fmtTotals(sumByCurrency(o.services))} · Запчастини {fmtTotals(sumByCurrency(o.parts))}</span>
        <span className="font-head font-black text-2xl">{fmtTotals(o.totals_by_currency || sumByCurrency([...o.services, ...o.parts]))}</span>
      </Card>

      {lb && (
        <PhotoLightbox
          photos={lb.photos}
          index={lb.index}
          onClose={() => setLb(null)}
          onNavigate={(i) => setLb((s) => ({ ...s, index: i }))}
        />
      )}
    </div>
  );
}
